import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Save, Router, CheckCircle2, XCircle, Loader2, Lock, Wifi } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext"; 

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const { user } = useAuth(); 
  const isAdmin = user?.role === 'admin';

  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [connected, setConnected] = useState(false);
  
  // These initial values match your hardcoded server.py variables
  const [formData, setFormData] = useState({
    host: "student-wifi.t2g.net.ph", 
    port: 8729, 
    username: "hotspot_api", 
    password: "", 
    use_ssl: true
  });

  useEffect(() => {
    checkConnectionStatus();
  }, []);

  const checkConnectionStatus = async () => {
    setTesting(true);
    try {
      // Hits your working /api/dashboard/stats endpoint
      const res = await axios.get(`${API}/dashboard/stats`);
      setConnected(res.data.connection_status);
    } catch (error) {
      setConnected(false);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!isAdmin) return; 
    
    setLoading(true);
    try {
      // NOTE: This will fail unless you add @api_router.post("/settings") to your server.py
      await axios.post(`${API}/settings`, formData);
      toast.success("Settings saved to database");
      checkConnectionStatus(); 
    } catch (error) {
      toast.error("Endpoint missing: Add /api/settings to your server.py");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6 animate-in fade-in duration-500">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Router Settings</h1>
          <p className="text-slate-500 mt-1 font-medium">MikroTik API Configuration — TAU Hotspot</p>
        </div>
        
        {!isAdmin && (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 gap-2 py-2 px-4 shadow-sm">
            <Lock className="w-4 h-4" /> Restricted: Operator Mode
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        <Card className="lg:col-span-2 border-slate-200 shadow-sm overflow-hidden">
          <CardHeader className="bg-slate-50/50 border-b border-slate-100">
            <CardTitle className="flex items-center gap-2 text-[#1a3d2e]">
              <Router className="w-5 h-5" /> Connection Infrastructure
            </CardTitle>
            <CardDescription>
              {isAdmin ? "Update router credentials. Changes affect live synchronization." : "Connection parameters are read-only for security."}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSave} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-slate-700 font-bold">Router IP / Domain</Label>
                  <Input 
                    value={isAdmin ? formData.host : "••••••••••••••••"} 
                    onChange={(e) => setFormData({...formData, host: e.target.value})} 
                    disabled={!isAdmin} 
                    className={!isAdmin ? "bg-slate-50 text-slate-400 font-mono tracking-widest" : "border-slate-300"}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700 font-bold">API Port</Label>
                  <Input 
                    type={isAdmin ? "number" : "text"}
                    value={isAdmin ? formData.port : "••••"} 
                    onChange={(e) => setFormData({...formData, port: parseInt(e.target.value)})} 
                    disabled={!isAdmin}
                    className={!isAdmin ? "bg-slate-50 text-slate-400 font-mono" : "border-slate-300"}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-700 font-bold">API Username</Label>
                <Input 
                  value={isAdmin ? formData.username : "••••••••"} 
                  onChange={(e) => setFormData({...formData, username: e.target.value})} 
                  disabled={!isAdmin}
                  className={!isAdmin ? "bg-slate-50 text-slate-400 font-mono" : "border-slate-300"}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-700 font-bold">API Password</Label>
                <div className="relative">
                  <Input 
                    type="password" 
                    value={isAdmin ? formData.password : ""} 
                    placeholder={isAdmin ? "Enter new password to update" : "••••••••••••"}
                    onChange={(e) => setFormData({...formData, password: e.target.value})} 
                    disabled={!isAdmin}
                    className={!isAdmin ? "bg-slate-50" : "border-slate-300"}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-emerald-50/30 rounded-xl border border-emerald-100">
                <div className="space-y-0.5">
                  <Label className="text-sm font-bold text-emerald-900">Secure Connection (SSL)</Label>
                  <div className="text-xs text-emerald-600">Encrypted communication via Port 8729</div>
                </div>
                <Switch 
                  checked={formData.use_ssl} 
                  disabled={!isAdmin} 
                  onCheckedChange={(val) => setFormData({...formData, use_ssl: val})} 
                />
              </div>

              <div className="flex gap-4 pt-2">
                {isAdmin && (
                  <Button type="submit" className="bg-[#1a3d2e] hover:bg-[#2e5c44] text-white shadow-md" disabled={loading}>
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Configuration
                  </Button>
                )}
                
                <Button type="button" variant="outline" onClick={checkConnectionStatus} disabled={testing} className="border-slate-300">
                  {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wifi className="w-4 h-4 mr-2" />}
                  Check Status
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Status Sidebar */}
        <div className="space-y-6">
          <Card className={`border-l-4 shadow-md transition-all duration-500 ${connected ? "border-l-emerald-500" : "border-l-red-500"}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${connected ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-600"}`}>
                  {connected ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                </div>
                <div>
                  <CardTitle className="text-base font-bold">
                    {connected ? "Router Connected" : "Router Offline"}
                  </CardTitle>
                  <CardDescription className="text-xs">
                    {connected ? "API Handshake Verified" : "Verification Failed"}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>
        </div>

      </div>
    </div>
  );
}